import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Icon, Button, StickyBottomBar, PriceDisplay, StarRating, Spinner,
  Counter, ModalWrapper, DateTimePicker, RouteEditSection, SEO,
} from '../../../components/common';
import RouteEditModal from '../../../components/service/RouteEditModal';
import { buildTourJsonLd, buildBreadcrumbJsonLd, JsonLd } from '../../../components/common/SEO';
import { tourService, reviewService } from '../../../api/services';
import { useMoney } from '../../../hooks/useMoney';
import { useDeepLTranslated } from '../../../hooks/useDeepLTranslated';
import { useAvailabilityCheck } from '../../../hooks/useAvailabilityCheck';
import { parseNoticeLines } from '../../../utils/noticeFormat';

export default function TourDetail() {
  const { t } = useTranslation();
  const money = useMoney();
  const tourTabs = [
    { id: 'info', label: t('tour.tab_info') },
    { id: 'schedule', label: t('tour.tab_schedule') },
    { id: 'notice', label: t('tour.tab_notice') },
    { id: 'reviews', label: t('tour.tab_reviews') },
  ];
  const { id } = useParams();
  const navigate = useNavigate();
  // BugHerd #106: 첫 진입 시 default 탭이 '투어 일정' → '투어 정보' 로 변경.
  const [activeTab, setActiveTab] = useState('info');
  const [reviews, setReviews] = useState([]);
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // 상단 갤러리: 이미지별 세로형 여부 캐시(index→bool). 세로형만 가로 비율로 크롭(아래 참조).
  const [imgPortrait, setImgPortrait] = useState({});

  // 예약 설정 state
  const [dateTime, setDateTime] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}T10:00`;
  });
  const [peopleQty, setPeopleQty] = useState(2);

  // 투어 일정 편집 state — BookingSettings 와 동일 패턴 (address + name 별도 보관)
  const [departure, setDeparture] = useState('');
  const [departureName, setDepartureName] = useState('');
  const [departureNotes, setDepartureNotes] = useState('');
  const [arrival, setArrival] = useState('');
  const [arrivalName, setArrivalName] = useState('');
  const [stopovers, setStopovers] = useState([]);
  const [showCourseDropdown, setShowCourseDropdown] = useState(true);
  const [editModal, setEditModal] = useState(null); // 'departure' | 'arrival'
  const [stopoverEditId, setStopoverEditId] = useState(null);

  // 이용 후기 모달
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);

  useEffect(() => {
    setLoading(true);
    tourService.getById(id)
      .then((data) => setTour(data))
      .catch(() => {})
      .finally(() => setLoading(false));
    reviewService.getList({ size: 20 }).then((res) => {
      const items = Array.isArray(res) ? res : res.items || [];
      setReviews(items.map((r) => ({
        name: r.username ? r.username.charAt(0) + '*'.repeat(Math.max(0, (r.username.length || 2) - 1)) : t('tour.anonymous'),
        rating: r.rating,
        text: r.content,
        date: r.created_at ? new Date(r.created_at).toLocaleDateString('ko-KR') : '',
      })));
    }).catch(() => {});
  }, [id, t]);

  // 삭제됐거나 존재하지 않는 투어 URL(예: 구글이 색인한 옛 /tour/3) →
  // 빈 페이지 대신 투어 목록으로 리다이렉트. 404·로드 실패 모두 커버.
  useEffect(() => {
    if (!loading && !tour) navigate('/tour', { replace: true });
  }, [loading, tour, navigate]);

  // tour 로드 후 일정 초기화
  useEffect(() => {
    if (!tour?.itinerary?.length) return;
    const items = tour.itinerary;
    setDeparture(items[0]?.address || items[0]?.title || '');
    setArrival(items[items.length - 1]?.address || items[items.length - 1]?.title || '');
    if (items.length > 2) {
      setStopovers(items.slice(1, -1).map((item, i) => ({
        id: Date.now() + i,
        address: item.address || item.title || '',
      })));
    }
  }, [tour]);

  // #166: tour 로드 시 peopleQty 가 어드민 min_passengers 미만이면 자동 맞춤.
  useEffect(() => {
    if (!tour) return;
    const minP = Number(tour.min_passengers) || 1;
    setPeopleQty((prev) => (prev < minP ? minP : prev));
  }, [tour]);

  // 추천 코스(백엔드 한국어 배열) → 현재 UI 언어로 DeepL 번역
  // 훅 규칙상 조기 return 이전에 호출해야 하므로 여기서 계산.
  // 어드민이 입력한 itinerary (JSONB list of {time, icon, title, desc, address}) 우선 사용.
  // 없으면 legacy recommended_courses (string array) fallback.
  const recommendedCoursesBundle = useMemo(
    () => {
      const itin = tour?.itinerary;
      if (Array.isArray(itin) && itin.length > 0) {
        return itin.map((step) => {
          if (typeof step === 'string') return step;
          if (step && typeof step === 'object') {
            const time = step.time ? `${step.time} ` : '';
            const title = step.title || '';
            const desc = step.desc ? ` — ${step.desc}` : '';
            return `${time}${title}${desc}`.trim();
          }
          return '';
        }).filter(Boolean);
      }
      return tour?.recommended_courses || [];
    },
    [tour],
  );
  const recommendedCourses = useDeepLTranslated(recommendedCoursesBundle);

  // 투어명/카테고리/설명 + notices/cancellation_policy JSONB 내 한국어 문자열을 일괄 번역.
  // BE apply_translations 가 처리하지 못하는 JSONB 콘텐츠와 Translation row 미존재 fallback 을 함께 커버.
  const localizableBundle = useMemo(() => {
    if (!tour) return [];
    const bag = [];
    // BugHerd #214: 투어명(tour.name)은 BE 번역명을 직접 사용 — DeepL 이중번역 제거. bundle 에 미포함.
    if (tour.category) bag.push(tour.category);
    if (tour.description) bag.push(tour.description);
    const n = tour.notices;
    if (n && typeof n === 'object' && !Array.isArray(n)) {
      // BugHerd #93: Notice 섹션 타이틀 다국어 적용 — i18n key 미정의로 ENG/JA/ZH/ES 에서 한국어 잔존하던 회귀.
      if ((n.vehicle || []).length) bag.push('차량 정보');
      if ((n.caution || []).length) bag.push('주의 사항');
      if ((n.tour || []).length) bag.push('투어 사항');
      // #260: 렌더는 parseNoticeLines 로 '- '/들여쓰기 마커를 떼고 tx(스트립된 텍스트)로 조회하므로,
      //   번역 번들도 동일하게 '마커 제거된' 텍스트를 넣어야 키가 일치한다.
      //   (#239 마이그레이션이 '- ' 를 주입한 뒤 번들=마커포함 / 렌더=마커제거 로 어긋나 한국어로 노출되던 회귀 수정)
      ['vehicle', 'caution', 'tour'].forEach((k) => {
        parseNoticeLines(n[k] || []).forEach((ln) => {
          if (ln.kind === 'space') return;
          const t = typeof ln.item === 'string' ? ln.item : (ln.item && typeof ln.item.text === 'string' ? ln.item.text : null);
          if (t) bag.push(t);
        });
      });
      (n.refund || []).forEach((it) => {
        if (it && typeof it === 'object') {
          if (it.period) bag.push(it.period);
          if (it.ratio) bag.push(it.ratio);
        } else if (typeof it === 'string') bag.push(it);
      });
    } else if (Array.isArray(n)) {
      n.forEach((it) => { if (typeof it === 'string') bag.push(it); });
    }
    (Array.isArray(tour.cancellation_policy) ? tour.cancellation_policy : []).forEach((it) => {
      if (typeof it === 'string') bag.push(it);
    });
    return bag;
  }, [tour]);
  const localizedList = useDeepLTranslated(localizableBundle);
  const tx = useMemo(() => {
    const m = new Map();
    localizableBundle.forEach((src, i) => { m.set(src, localizedList[i] || src); });
    return (s) => (typeof s === 'string' ? (m.get(s) || s) : s);
  }, [localizableBundle, localizedList]);

  // 후기 모달용 — 본문 한국어를 현재 UI 언어로 DeepL 번역 (Reviews.js / MyReviews.js 동일 패턴)
  const reviewTextsBundle = useMemo(() => reviews.map((r) => r.text || ''), [reviews]);
  const tReviewTexts = useDeepLTranslated(reviewTextsBundle);

  // 어드민에서 업로드한 이미지는 image_type 이 null 인 경우가 있어 gallery 외 null 도 갤러리로 취급.
  const galleryImagesCount = ((tour?.images || []).filter((img) => !img.image_type || img.image_type === 'gallery')).length;
  const allImagesCount = (tour?.image_url ? 1 : 0) + galleryImagesCount;

  // 갤러리 2장 이상이면 4초 간격으로 자동 슬라이드. 1장이면 슬라이드 의미 없음.
  // (Hooks 규칙: early return 보다 위에 선언 — conditional 호출 금지)
  useEffect(() => {
    if (allImagesCount <= 1) return undefined;
    const timer = setInterval(() => {
      setCurrentImageIndex((i) => (i + 1) % allImagesCount);
    }, 4000);
    return () => clearInterval(timer);
  }, [allImagesCount]);

  // 가용성(휴무/예약충돌) 체크 — 일정 & 예약하기 클릭 시점에 차단, Transfer 흐름과 동일 컴포넌트 재사용.
  // Hooks 규칙: early return(loading) 이전에 호출해야 함 — 이전엔 if (loading) 아래에 있어
  // 첫 렌더(loading=true) 시 hook 미호출 → 두 번째 렌더(loading=false) 에서 hook 추가됨 → React error #310.
  const { check: checkAvailability, modal: availabilityModal } = useAvailabilityCheck({
    serviceType: 'tour',
    dateTime,
    passengers: peopleQty,
    onSelectOtherDate: () => {},
  });

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><Spinner size="lg" /></div>;
  // 투어 없음(삭제/404) → 위 useEffect 가 /tour 로 리다이렉트. 그 찰나 스피너 노출(빈 페이지 방지).
  if (!tour) return <div className="min-h-screen bg-white flex items-center justify-center"><Spinner size="lg" /></div>;

  const tourName = tour?.name || '';
  // BugHerd #107/#108: 상단 제목 + 우측 예약 카드 제목의 "[프라이빗투어]" prefix 삭제 요청.
  // 기존엔 카테고리를 자동 prefix 로 표시했으나 클라가 단순 투어명만 노출 원함.
  const tourTitle = tourName;

  // #166 / 가격 자동 합산:
  // - 기본가격(price)
  // - 추가인원 가산: max(0, peopleQty - min_passengers) × extra_passenger_price
  // - 주말 가산: dateTime 이 토/일 이면 weekend_surcharge
  // - 야간 가산: dateTime 이 야간 시간대(기본 18:00~06:00)이면 night_surcharge
  const tourBasePrice = Number(tour?.price) || 0;
  const tourMinPax = Number(tour?.min_passengers) || 1;
  const tourMaxPax = Number(tour?.max_passengers) || 6;
  const tourExtraPaxPrice = Number(tour?.extra_passenger_price) || 0;
  const tourWeekendSurcharge = Number(tour?.weekend_surcharge) || 0;
  const tourNightSurcharge = Number(tour?.night_surcharge) || 0;
  // 야간 시간 — 어드민 입력 없으면 18:00~06:00 전역 기본
  const _nightStart = (tour?.night_start_time || '18:00').toString().slice(0, 5);
  const _nightEnd = (tour?.night_end_time || '06:00').toString().slice(0, 5);
  // dateTime: "YYYY-MM-DDTHH:MM" (ISO 부분형식 — 로컬로 파싱)
  const _dt = dateTime ? new Date(dateTime) : null;
  const _isWeekend = _dt && !Number.isNaN(_dt.getTime()) && (_dt.getDay() === 0 || _dt.getDay() === 6);
  const _hhmm = dateTime ? dateTime.slice(11, 16) : '';
  // 야간 판정: start < end 면 [start, end) 안. start > end (자정 넘김) 이면 [start, 24) ∪ [0, end)
  const _isNight = _hhmm && (() => {
    if (_nightStart < _nightEnd) return _hhmm >= _nightStart && _hhmm < _nightEnd;
    return _hhmm >= _nightStart || _hhmm < _nightEnd;
  })();
  // #166: 기본 포함 인원(min_passengers) 초과분만 가산 — 백엔드(tours.py)·예약 페이지와 동일 기준.
  const tourExtraCount = Math.max(0, peopleQty - tourMinPax);
  const tourPrice =
    tourBasePrice +
    tourExtraCount * tourExtraPaxPrice +
    (_isWeekend ? tourWeekendSurcharge : 0) +
    (_isNight ? tourNightSurcharge : 0);
  const galleryImages = (tour?.images || []).filter((img) => !img.image_type || img.image_type === 'gallery').sort((a, b) => a.sort_order - b.sort_order);
  const detailImages = tour?.detail_images || [];

  const allImages = [];
  if (tour?.image_url) allImages.push({ url: tour.image_url, alt: tourName });
  galleryImages.forEach((img) => allImages.push({ url: img.image_url, alt: img.caption || '' }));

  const handleBooking = async () => {
    // Transfer BookingSettings.js:408 와 동일 — 과거 시간 예약 차단
    if (dateTime && new Date(dateTime.replace('T', ' ')) <= new Date()) {
      alert(t('booking.alert_expired'));
      return;
    }
    const available = await checkAvailability();
    if (!available) return;
    // #190: 상세 URL 은 slug 일 수 있으나 booking/payment 는 숫자 id 로 통일(결제 플로우 무영향, noindex).
    navigate(`/tour/${tour?.id || id}/booking`, {
      state: {
        tour,
        dateTime,
        peopleQty,
        departure,
        departureName,
        arrival,
        arrivalName,
        stopovers,
      },
    });
  };
  const prevImage = () => setCurrentImageIndex((i) => (i - 1 + allImages.length) % allImages.length);
  const nextImage = () => setCurrentImageIndex((i) => (i + 1) % allImages.length);

  const handleTabChange = (tabId) => {
    if (tabId === 'reviews') { setShowReviewModal(true); return; }
    setActiveTab(tabId);
  };

  const addStopover = (index) => {
    const newStop = { id: Date.now(), address: '', name: '' };
    setStopovers((prev) => {
      const next = [...prev];
      next.splice(typeof index === 'number' ? index : prev.length, 0, newStop);
      return next;
    });
    // 신규 row 생성 직후 검색 모달 자동 오픈 (BookingSettings 와 동일 UX).
    setStopoverEditId(newStop.id);
    setEditModal('stopover');
  };
  const removeStopover = (sid) => setStopovers((prev) => prev.filter((s) => s.id !== sid));
  const updateStopover = (sid, address, name) =>
    setStopovers((prev) => prev.map((s) => s.id === sid ? { ...s, address, name: name ?? s.name ?? '' } : s));

  const handleAddressConfirm = (newAddress, newName) => {
    if (editModal === 'departure') {
      setDeparture(newAddress);
      setDepartureName(newName || '');
    } else if (editModal === 'arrival') {
      setArrival(newAddress);
      setArrivalName(newName || '');
    } else if (stopoverEditId) {
      updateStopover(stopoverEditId, newAddress, newName || '');
    }
    setEditModal(null);
    setStopoverEditId(null);
  };

  // 사이드바 콘텐츠 (모바일/데스크탑 공통)
  const sidebarContent = (
    <div className="space-y-4">
      <h2 className="text-base font-bold text-mono-900 leading-snug">{tourTitle}</h2>

      {/* 이용 일시 */}
      <div>
        <span className="text-2sm font-medium text-mono-600 block mb-1.5">{t('tour.use_datetime')}</span>
        <DateTimePicker value={dateTime} onChange={setDateTime} maxMonths={2} blockHolidays />
      </div>

      {/* 이용 인원 — #166: 어드민 min/max 반영. 카운터 max 는 정원초과 추가요금 대응 위해 +4 까지 허용. */}
      <div>
        <span className="text-2sm font-medium text-mono-600 flex items-center gap-3 mb-1.5">
          {t('tour.use_passengers')}
          <span className="text-mono-400 font-normal text-xs">{t('tour.detail_max_pax_count', { count: Math.min(tourMaxPax, 6) })}</span>
        </span>
        {/* #166: 차량 정원 6명 상한(모든 투어). */}
        <Counter value={peopleQty} onChange={setPeopleQty} min={tourMinPax} max={Math.min(tourMaxPax, 6)} />
      </div>

      <div className="border-t border-mono-100" />

      {/* 예상 결제액 */}
      <div>
        <span className="text-2sm text-mono-500">{t('tour.estimated_payment')}</span>
        <p className="flex items-baseline gap-1 mt-0.5">
          {/* #272: USD(환산 추정)일 때만 ~ 표시 — 원화는 정확액이라 미표시 (PriceDisplay 와 동일 규칙) */}
          <span className="text-2xl font-bold text-mono-900">{money(tourPrice)}{/^US\$/.test(String(money(tourPrice))) ? ' ~' : ''}</span>
        </p>
      </div>

      <Button fullWidth onClick={handleBooking} disabled={!dateTime}>
        <span className="text-base font-bold">{t('tour.schedule_book')}</span>
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {tour && (
        <SEO
          titleOverride={`${tourTitle} | VIP Van Tour`}
          descriptionOverride={tour.summary || tx(tour.description) || undefined}
          renderH1
          image={tour.image_url || allImages[0]?.url}
          jsonLd={buildTourJsonLd({
            name: tourTitle,
            description: tour.summary || tx(tour.description) || undefined,
            image: tour.image_url || allImages[0]?.url,
            price: tour.price,
            url: typeof window !== 'undefined' ? window.location.href.split('?')[0] : undefined,
            // aggregateRating: BE 가 tour.average_rating·review_count 제공 시 연결 (현재 미제공 → 생략)
          })}
        />
      )}
      {/* 빵부스러기 구조화 데이터 (메타 중복 없이 JSON-LD 만 추가) */}
      {tour && (
        <JsonLd data={buildBreadcrumbJsonLd([
          { name: t('nav.home', { defaultValue: 'Home' }), url: (typeof window !== 'undefined' ? window.location.origin : 'https://vipvantour.com') },
          { name: t('nav.tour', { defaultValue: 'Tours' }), url: (typeof window !== 'undefined' ? window.location.origin : 'https://vipvantour.com') + '/tour' },
          { name: tourTitle },
        ])} />
      )}
      {/* Mobile Back Nav */}
      <div className="sm:hidden flex items-center h-10 px-2.5 border-b border-mono-100">
        <button onClick={() => navigate(-1)} className="p-1">
          <Icon name="chevron-left" size={20} className="text-mono-900" />
        </button>
      </div>

      <div className="max-w-content mx-auto px-0 sm:px-5 py-0 sm:py-8">
        {/* Desktop Breadcrumb */}
        <button
          onClick={() => navigate(-1)}
          className="hidden sm:flex items-center gap-2 text-mono-500 hover:text-mono-900 transition mb-6 max-w-full"
        >
          <Icon name="chevron-left" size={20} className="shrink-0" />
          <span className="text-xl sm:text-2xl font-bold text-mono-900 truncate min-w-0">{tourTitle}</span>
        </button>

        {/* 메인 레이아웃 */}
        <div className="flex flex-col lg:flex-row lg:gap-8">

          {/* 좌측: 이미지 + 탭 + 탭 콘텐츠 */}
          <div className="flex-1 min-w-0 lg:max-w-[640px]">

            {/* 이미지 슬라이드 */}
            <div className="relative w-full bg-mono-100 sm:rounded-xl overflow-hidden">
              {allImages.length > 0 ? (
                <>
                  {/* 세로형(높이>너비) 이미지만 가로 4:3 으로 상하 크롭 → 옆으로 넘길 때 높이 들쭉날쭉 방지.
                      가로형은 원본 비율 유지(잘림 없음). 방향은 onLoad 의 naturalW/H 로 판별·캐시. */}
                  <div className={imgPortrait[currentImageIndex] ? 'w-full aspect-[4/3] overflow-hidden' : 'w-full'}>
                    <img
                      src={allImages[currentImageIndex].url}
                      alt={allImages[currentImageIndex].alt}
                      onLoad={(e) => {
                        const w = e.currentTarget.naturalWidth;
                        const h = e.currentTarget.naturalHeight;
                        if (w && h) {
                          const portrait = h > w;
                          setImgPortrait((m) => (m[currentImageIndex] === portrait ? m : { ...m, [currentImageIndex]: portrait }));
                        }
                      }}
                      className={imgPortrait[currentImageIndex]
                        ? 'w-full h-full object-cover block'
                        : 'w-full h-auto block max-h-[80vh] object-contain'}
                    />
                  </div>
                  {allImages.length > 1 && (
                    <>
                      <button onClick={prevImage} className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center">
                        <Icon name="chevron-left" size={18} className="text-white" />
                      </button>
                      <button onClick={nextImage} className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center">
                        <Icon name="chevron-right" size={18} className="text-white" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {allImages.map((_, i) => (
                          <button key={i} onClick={() => setCurrentImageIndex(i)}
                            className={`w-2 h-2 rounded-full transition ${i === currentImageIndex ? 'bg-white' : 'bg-white/40'}`} />
                        ))}
                      </div>
                      <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
                        {currentImageIndex + 1} / {allImages.length}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full aspect-[16/9] flex items-center justify-center">
                  <div className="text-center space-y-2">
                    <Icon name="image" size={48} className="text-mono-400 mx-auto" />
                    <p className="text-sm text-mono-400">{t('tour.product_image')}</p>
                  </div>
                </div>
              )}
            </div>

            {/* 모바일 사이드바 */}
            <div className="lg:hidden px-2.5 py-4 sm:px-0 sm:py-5">
              <div className="bg-white sm:rounded-xl sm:border sm:border-mono-200 p-4 sm:p-5">
                {sidebarContent}
              </div>
            </div>

            {/* 탭 네비게이션 */}
            <div className="flex border-b border-mono-100 mt-5 sm:mt-8">
              {tourTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex-1 h-11 text-[14px] font-medium whitespace-nowrap transition relative flex items-center justify-center ${
                    activeTab === tab.id && tab.id !== 'reviews' ? 'text-orange-500' : 'text-mono-500 hover:text-mono-900'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && tab.id !== 'reviews' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />
                  )}
                </button>
              ))}
            </div>

            {/* 탭 콘텐츠 */}
            <div className="py-5 sm:py-8 px-2.5 sm:px-0">

              {/* === 프라이빗 투어 정보 === */}
              {activeTab === 'info' && (
                <div>
                  {detailImages.length > 0 ? (
                    <div className="space-y-2">
                      {detailImages.map((img) => (
                        <img key={img.id} src={img.image_url} alt={img.caption || ''} loading="lazy" className="w-full rounded-xl" />
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center min-h-[300px] bg-mono-50 rounded-xl">
                      <Icon name="image" size={48} className="text-mono-300 mb-3" />
                      <p className="text-sm text-mono-400">{t('tour.tour_detail_image')}</p>
                      <p className="text-xs text-mono-300 mt-1">{t('tour.tour_detail_image_hint')}</p>
                    </div>
                  )}
                </div>
              )}

              {/* === 투어 일정 === */}
              {activeTab === 'schedule' && (
                <div className="space-y-5">
                  {/* 헤더 */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-1 h-5 bg-orange-500 rounded-full shrink-0" />
                      <h3 className="text-base font-bold text-mono-900">{t('tour.schedule_title')}</h3>
                    </div>
                    <p className="text-sm text-mono-500 ml-3">{t('tour.schedule_desc')}</p>
                  </div>

                  {/* 추천 코스 */}
                  <div className="border border-mono-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setShowCourseDropdown(!showCourseDropdown)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-white"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                          <Icon name="recommend" size={12} className="text-orange-500" />
                        </div>
                        <span className="text-sm font-medium text-mono-900">{t('tour.recommended_course')}</span>
                      </div>
                      <Icon name={showCourseDropdown ? 'chevron-up' : 'chevron-down'} size={16} className="text-mono-400" />
                    </button>
                    {showCourseDropdown && (
                      <div className="px-4 py-3 bg-mono-50 border-t border-mono-100">
                        {recommendedCourses.length > 0 ? (
                          <ul className="space-y-2">
                            {recommendedCourses.map((course, i) => {
                              // #231: 스케줄(시간 기준)은 기본 불릿 없이 깔끔하게 줄바꿈 정렬.
                              //   명시 마커(-, *, •)로 시작하는 줄만 불릿 표시(마커는 제거).
                              const m = String(course ?? '').match(/^\s*[-*•]\s+([\s\S]*)$/);
                              const text = m ? m[1] : String(course ?? '');
                              return (
                                <li key={i} className="text-sm text-mono-500 leading-relaxed flex items-start gap-2">
                                  {m && <span className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0 mt-[7px]" />}
                                  <span className="flex-1 min-w-0 whitespace-pre-line break-words break-keep">{text}</span>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="text-sm text-mono-400">{t('tour.no_recommended')}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 1일차 헤더 */}
                  <div className="bg-orange-500 rounded-lg px-4 py-2.5">
                    <span className="text-sm font-bold text-white">{t('tour.day_1')}</span>
                  </div>

                  {/* 경로 편집 — BookingSettings의 RouteEditSection과 동일 */}
                  <RouteEditSection
                    directionLabel={t('tour.movement_route')}
                    departure={{ name: departureName, address: departure }}
                    arrival={{ label: t('form.destination'), name: arrivalName, address: arrival, icon: 'place' }}
                    stopovers={stopovers}
                    onAddStopover={addStopover}
                    onRemoveStopover={removeStopover}
                    onEditDeparture={() => setEditModal('departure')}
                    onEditArrival={() => setEditModal('arrival')}
                    onEditStopover={(sid) => { setStopoverEditId(sid); setEditModal('stopover'); }}
                    notes={departureNotes}
                    onNotesChange={(e) => setDepartureNotes(e.target.value)}
                  />
                </div>
              )}

              {/* === 유의사항 === */}
              {activeTab === 'notice' && (() => {
                // 어드민 저장 형식: notices = {vehicle:[], caution:[], tour:[], refund:[{period, ratio}]}
                // legacy 시드 형식: notices = ['...', '...']  (string array)
                const n = tour?.notices;
                const isDict = n && typeof n === 'object' && !Array.isArray(n);
                const sections = isDict
                  ? [
                      // 차량/투어 라벨은 DeepL fallback. 주의 사항은 i18n key 사전 정의
                      // — DeepL이 영문을 "Caveats" 등 어색하게 번역하던 회귀 차단.
                      { title: tx('차량 정보'), items: n.vehicle || [] },
                      { title: t('tour.section_caution'), items: n.caution || [] },
                      { title: tx('투어 사항'), items: n.tour || [] },
                    ].filter((s) => Array.isArray(s.items) && s.items.length > 0)
                  : Array.isArray(n) && n.length > 0
                    ? [{ title: t('tour.notice'), items: n }]
                    : [];
                // 환불 규정 — 어드민 dict 형식의 refund 우선, legacy cancellation_policy fallback
                const refundFromDict = isDict && Array.isArray(n.refund) ? n.refund : null;
                const refundLegacy = Array.isArray(tour?.cancellation_policy) ? tour.cancellation_policy : null;
                const refundItems = refundFromDict || refundLegacy || [];
                return (
                  <div className="space-y-6">
                    <h3 className="text-lg sm:text-xl font-extrabold text-mono-900">{t('tour.notice')}</h3>
                    <div className="space-y-4">
                      {sections.map((sec, idx) => (
                        <div key={idx} className="bg-blue-900 rounded-xl p-4 sm:p-5 overflow-hidden">
                          <h4 className="text-sm font-bold text-white mb-3">{sec.title}</h4>
                          <ul className="space-y-2">
                            {/* #239: 불릿/문장/들여쓰기/문단 간격 지원. 기본은 맨 텍스트, '- ' 마커 줄만 불릿.
                                #188: min-w-0 + break-words 로 가로 overflow 방지(양쪽 분기 모두 유지). */}
                            {parseNoticeLines(sec.items).map((ln, i) => {
                              if (ln.kind === 'space') return <li key={i} className="h-2" aria-hidden="true" />;
                              const padCls = ['', 'pl-4', 'pl-8', 'pl-12'][ln.indent] || '';
                              const content = typeof ln.item === 'string'
                                ? tx(ln.item)
                                : (ln.item?.text ? tx(ln.item.text) : JSON.stringify(ln.item));
                              if (ln.kind === 'text') {
                                return (
                                  <li key={i} className={`text-sm text-white/80 min-w-0 break-words break-keep ${padCls}`}>
                                    {content}
                                  </li>
                                );
                              }
                              return (
                                <li key={i} className={`text-sm text-white/80 flex items-start gap-2 ${padCls}`}>
                                  <span className="w-1 h-1 rounded-full bg-white/50 shrink-0 mt-2" />
                                  <span className="flex-1 min-w-0 break-words break-keep">{content}</span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                      {refundItems.length > 0 && (
                        <div className="bg-blue-900 rounded-xl p-4 sm:p-5 overflow-hidden">
                          <h4 className="text-sm font-bold text-white mb-3">{t('tour.cancel_refund_rules')}</h4>
                          <ul className="space-y-2">
                            {refundItems.map((item, i) => {
                              if (typeof item === 'string') {
                                return (
                                  <li key={i} className="text-sm text-white/80 flex items-start gap-2">
                                    <span className="w-1 h-1 rounded-full bg-white/50 shrink-0 mt-2" />
                                    <span className="flex-1 min-w-0 break-words break-keep">{tx(item)}</span>
                                  </li>
                                );
                              }
                              const period = item?.period ? tx(item.period) : '';
                              const ratio = item?.ratio ? tx(item.ratio) : '';
                              return (
                                <li key={i} className="text-sm text-white/80 flex items-start gap-2">
                                  <span className="w-1 h-1 rounded-full bg-white/50 shrink-0 mt-2" />
                                  <span className="flex-1 min-w-0 break-words break-keep">
                                    {`${period}${period && ratio ? ' · ' : ''}${ratio}`}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* 우측: 데스크탑 sticky 사이드바 */}
          <div className="hidden lg:block w-[260px] shrink-0">
            <div className="sticky top-[75px] bg-white rounded-xl border border-mono-200 p-5">
              {sidebarContent}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Bottom Bar */}
      <StickyBottomBar visibility="mobile-only">
        <PriceDisplay label={t('tour.estimated_payment')} amount={tourPrice} layout="stack" approx />
        <Button onClick={handleBooking} disabled={!dateTime}>{t('tour.schedule_book')}</Button>
      </StickyBottomBar>

      {/* Bottom padding for mobile sticky bar */}
      <div className="lg:hidden h-20" />

      {availabilityModal}

      {/* 경로 수정 모달 */}
      {(editModal === 'departure' || editModal === 'arrival' || editModal === 'stopover') && (
        <RouteEditModal
          type="tour"
          field={editModal === 'stopover' ? 'stopover' : editModal}
          currentValue={
            editModal === 'departure' ? departure
            : editModal === 'arrival' ? arrival
            : stopovers.find((s) => s.id === stopoverEditId)?.address || ''
          }
          onConfirm={handleAddressConfirm}
          onClose={() => { setEditModal(null); setStopoverEditId(null); }}
        />
      )}

      {/* 서비스 준비중 모달 */}
      {showServiceModal && (
        <ModalWrapper onClose={() => setShowServiceModal(false)} padding="p-5 sm:p-8" gap="gap-5">
          <h2 className="text-xl font-bold text-mono-900 text-center">{t('tour.guide')}</h2>
          <p className="text-sm text-mono-500 text-center leading-relaxed break-keep whitespace-pre-line">
            {t('tour.service_coming_soon')}
          </p>
          <div className="flex gap-3">
            <Button level="level3" fullWidth onClick={() => setShowServiceModal(false)}>{t('common.cancel')}</Button>
            <Button level="level1" fullWidth onClick={() => setShowServiceModal(false)}>{t('common.confirm')}</Button>
          </div>
        </ModalWrapper>
      )}

      {/* 이용 후기 모달 */}
      {showReviewModal && (
        <ModalWrapper onClose={() => setShowReviewModal(false)} padding="p-5 sm:p-6" gap="gap-4">
          <h3 className="text-lg font-bold text-mono-900 text-center">{t('tour.review_title_modal')}</h3>
          {reviews.length === 0 ? (
            <p className="text-sm text-mono-400 text-center py-8">{t('tour.no_reviews')}</p>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {reviews.map((review, idx) => (
                <div key={idx} className="border-b border-mono-100 pb-4 last:border-0 last:pb-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-sm font-bold text-mono-900">{review.name}</span>
                    <StarRating rating={review.rating} size={14} showEmpty={false} />
                  </div>
                  <p className="text-sm text-mono-500 leading-relaxed">{tReviewTexts[idx] || review.text}</p>
                  <p className="text-2sm text-mono-400 mt-1.5">{review.date}</p>
                </div>
              ))}
            </div>
          )}
          <Button level="level1" fullWidth onClick={() => setShowReviewModal(false)}>{t('common.close')}</Button>
        </ModalWrapper>
      )}
    </div>
  );
}

